import _ from 'lodash';
import React, {FunctionComponent, useEffect} from 'react';
import {useSelector} from 'react-redux';

import {CatalogProductStatus, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {Title, TitleSize} from '@treez-ui/core/layout/Title';

import {formatCurrency} from '~/lib/format';
import {
    selectMergeProduct,
    selectMergeProductsStorePresence,
} from '~/redux/selectors/mergeSelectors';
import {selectStoresInOrganization} from '~/redux/selectors/organizationSelectors';
import {CatalogProductInformation, ProductsPresenceAcrossStores} from '~/utils/ProductAPI';
import {MergeProductInfoTable} from './catalogAssignment/MergeProductInfoTable';
import MergeStoreMatrix from './catalogAssignment/MergeStoreMatrix';

const {ACTIVE, DEACTIVATED, DELETED} = CatalogProductStatus;

interface Props {
    catalogProductInformation: CatalogProductInformation;
    products: ProductInCatalog[];
    setCatalogProductInformation: (catalogProductInformation: CatalogProductInformation) => void;
}

export enum TableFieldOptions {
    AMOUNT = 'amount',
    BRAND = 'brand',
    PRODUCT = 'product',
    SKU = 'sku',
}

export const tableFields = {
    amount: {
        dataField: 'amount',
        displayName: 'Amount',
    },
    brand: {
        dataField: 'brandName',
        displayName: 'Brand',
    },
    product: {
        dataField: 'name',
        displayName: 'Product',
    },
    sku: {
        dataField: 'sku',
        displayName: 'SKU',
    },
};

export const PRODUCT_TABLE_FIELDS = [
    TableFieldOptions.SKU,
    TableFieldOptions.PRODUCT,
    TableFieldOptions.BRAND,
    TableFieldOptions.AMOUNT,
];

export const CATALOG_ASSIGNMENT_DEFAULT_PRICE = '';

const CatalogAssignment: FunctionComponent<Props> = (props: Props) => {
    const {catalogProductInformation, products, setCatalogProductInformation} = props;

    const stores = useSelector(selectStoresInOrganization, _.isEqual);
    const storePresence = useSelector(
        selectMergeProductsStorePresence,
        _.isEqual
    ) as ProductsPresenceAcrossStores;
    const productToMergeTo = useSelector(
        selectMergeProduct,
        _.isEqual
    ) as Partial<ProductInCatalog>;

    const getValidMergeStores = () => {
        const storePresenceGroupByStoreId: ProductsPresenceAcrossStores =
            getStorePresenceGroupByStoreId(storePresence);

        const validMergeStoreIds: number[] = _.map(_.keys(storePresenceGroupByStoreId), Number);

        return _.filter(stores, (store) => validMergeStoreIds.includes(store.catalogId!));
    };

    const getStorePresenceGroupByStoreId = (storePresence: ProductsPresenceAcrossStores) => {
        return _.groupBy(_.flatMap(_.mapValues(storePresence)), 'id');
    };

    const getUniqStorePresencePricesByStore = (
        storePresenceGroupByStoreId: ProductsPresenceAcrossStores
    ) => {
        const storePresencesByStoreIdUniqPrice: ProductsPresenceAcrossStores = _.mapValues(
            storePresenceGroupByStoreId,
            (a) => _.uniqBy(a, 'price')
        );

        return storePresencesByStoreIdUniqPrice;
    };

    const getUniqStorePresenceStatusByStore = (
        storePresenceGroupByStoreId: ProductsPresenceAcrossStores
    ) => {
        const storePresencesByStoreIdUniqStatus: ProductsPresenceAcrossStores = _.mapValues(
            storePresenceGroupByStoreId,
            (a) => _.uniqBy(a, 'status')
        );

        return storePresencesByStoreIdUniqStatus;
    };

    const getUniqStorePresencePriceTierIdByStore = (
        storePresenceGroupByStoreId: ProductsPresenceAcrossStores
    ) => {
        const storePresencesByStoreIdUniqStatus: ProductsPresenceAcrossStores = _.mapValues(
            storePresenceGroupByStoreId,
            (a) => _.uniqBy(a, 'priceTierId')
        );

        return storePresencesByStoreIdUniqStatus;
    };

    const initializeStorePrice = (storePresence: ProductsPresenceAcrossStores) => {
        const storePresenceGroupByStoreId: ProductsPresenceAcrossStores =
            getStorePresenceGroupByStoreId(storePresence);

        const uniqStorePresencePricesByStore: ProductsPresenceAcrossStores =
            getUniqStorePresencePricesByStore(storePresenceGroupByStoreId);
        const uniqStorePresencePriceTierIdByStore: ProductsPresenceAcrossStores =
            getUniqStorePresencePriceTierIdByStore(storePresenceGroupByStoreId);
        const uniqStorePresenceStatusByStore: ProductsPresenceAcrossStores =
            getUniqStorePresenceStatusByStore(storePresenceGroupByStoreId);

        const stores = getValidMergeStores();

        return _.mapValues(_.keyBy(stores, 'catalogId'), (store) => {
            const pricesForStore = uniqStorePresencePricesByStore[store.catalogId!] || [];
            const priceTierIdForStore = uniqStorePresencePriceTierIdByStore[store.catalogId!] || [];
            const statusForStore = uniqStorePresenceStatusByStore[store.catalogId!] || [];

            let price = CATALOG_ASSIGNMENT_DEFAULT_PRICE;
            let status = ACTIVE;
            let priceTierId;

            if (_.size(priceTierIdForStore) === 1) {
                priceTierId = _.get(priceTierIdForStore, '[0].priceTierId');
            }

            if (_.size(pricesForStore) === 1) {
                price = _.get(pricesForStore, '[0].price', CATALOG_ASSIGNMENT_DEFAULT_PRICE);
            }

            if (_.size(statusForStore) === 1) {
                status = _.get(statusForStore, '[0].status', ACTIVE);

                // Set status as deactivated if defaulted to deleted
                if (status === DELETED) {
                    status = DEACTIVATED;
                }
            }

            if (priceTierId) {
                return {
                    priceTierId,
                    status,
                };
            }
            return {
                price: formatCurrency(price),
                status,
            };
        });
    };

    useEffect(() => {
        if (_.isEmpty(catalogProductInformation)) {
            setCatalogProductInformation(initializeStorePrice(storePresence));
        }
    }, []);

    const updatePrice = (catalogId: number, price: string | undefined) => {
        const catalogProduct = catalogProductInformation[catalogId];

        const updatedCatalogProduct = {
            ...catalogProduct,
            price,
            priceTierId: undefined,
        };

        setCatalogProductInformation({
            ...catalogProductInformation,
            [catalogId]: updatedCatalogProduct,
        });
    };

    const updatePriceTierId = (catalogId: number, priceTierId: number) => {
        const catalogProduct = catalogProductInformation[catalogId];

        const updatedCatalogProduct = {
            ...catalogProduct,
            price: undefined,
            priceTierId,
        };

        setCatalogProductInformation({
            ...catalogProductInformation,
            [catalogId]: updatedCatalogProduct,
        });
    };

    const updateStatus = (catalogId: number, isActive: boolean) => {
        const catalogProduct = catalogProductInformation[catalogId];

        const status = isActive ? ACTIVE : DEACTIVATED;

        const updatedCatalogProduct = {
            ...catalogProduct,
            status,
        };

        setCatalogProductInformation({
            ...catalogProductInformation,
            [catalogId]: updatedCatalogProduct,
        });
    };

    return (
        <div className="catalog-assignment-container">
            <div className="horizontal-center">
                <Title size={TitleSize.MEDIUM} text="ASSIGN PRICES FOR MERGED PRODUCT" />
            </div>
            <div className="horizontal-center">
                Enter product price for each store. Or select existing price (click on cell value).
            </div>
            <div className="products-container">
                <MergeProductInfoTable
                    productToMergeTo={productToMergeTo}
                    productsToMerge={products}
                    stores={getValidMergeStores()}
                />
                <MergeStoreMatrix
                    catalogProductInformation={catalogProductInformation}
                    productsToMerge={products}
                    storePresence={storePresence}
                    stores={getValidMergeStores()}
                    updatePrice={updatePrice}
                    updateStatus={updateStatus}
                    updatePriceTierId={updatePriceTierId}
                />
            </div>
        </div>
    );
};

export default CatalogAssignment;
